import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ChargeService } from 'app/core/services/charge.service';
import { ImportDataService } from 'app/core/services/import-data.service';
import { PrivateService } from 'app/core/services/private.service';
import { concatMap, delay, forkJoin, from, of, tap } from 'rxjs';

@Component({
  selector: 'modal-import-deduction',
  templateUrl: './modal-import-deduction.component.html',
  styleUrls: ['./modal-import-deduction.component.scss']
})
export class ModalImportDeductionComponent implements OnInit, AfterViewInit {
  @Input() importedData: any[] = [];
  @Input() type: 'deduction' | 'adjustment' = 'deduction';

  isLoading = false;
  formatError = false;
  insertionMessage: string = null;
  totalItems = 0;
  isInserting = false;
  displayTable = true;
  isImported = false;

  employees: any[] = [];
  charges: any[] = [];
  selectedItems: any[] = [];
  dataSource = new MatTableDataSource<any>([]);

  displayedColumns: string[] = [
    'select', 'name', 'description', 'employeeName', 'chargeName', 'chargeAmount', 'startDate', 'recurring'
  ];

  expectedHeaders: string[] = ['name', 'employeeName', 'chargeName', 'chargeAmount'];

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    public activeModal: NgbActiveModal,
    private importDataService: ImportDataService,
    private privateService: PrivateService,
    private chargeService: ChargeService
  ) {}

  ngOnInit(): void {
    this.dataSource = new MatTableDataSource(this.importedData);
    this.selectedItems = [...this.importedData];

    if (!this.isDataFormatCorrect()) {
      this.formatError = true;
    }

    forkJoin({
      employees: this.privateService.getEmployeeInfoAll(),
      charges: this.chargeService.getCharges()
    }).subscribe({
      next: ({ employees, charges }) => {
        this.employees = employees;
        this.charges = charges;
      }
    });
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  isDataFormatCorrect(): boolean {
    if (this.importedData.length === 0) return true;
    const importedHeaders = Object.keys(this.importedData[0]);
    return this.expectedHeaders.every(h => importedHeaders.includes(h));
  }

  onCheckboxChange(item: any, isChecked: boolean): void {
    if (isChecked) {
      this.selectedItems.push(item);
    } else {
      const index = this.selectedItems.indexOf(item);
      if (index !== -1) this.selectedItems.splice(index, 1);
    }
  }

  isSelected(item: any): boolean {
    return this.selectedItems.includes(item);
  }

  toggleSelectAll(isChecked: boolean): void {
    this.selectedItems = isChecked ? [...this.importedData] : [];
  }

  saveSelectedItems(): void {
    this.totalItems = this.selectedItems.length;
    let insertedCount = 0;
    this.isInserting = true;
    this.displayTable = false;
    this.isLoading = true;
    this.insertionMessage = 'Please wait...';

    const label = this.type === 'deduction' ? 'deduction' : 'adjustment';

    const mapped = this.selectedItems.map(row => {
      const employee = this.employees.find(e => e.employeeName === row.employeeName);
      const charge = this.charges.find(c => c.name === row.chargeName);
      return {
        id: 0,
        name: row.name || '',
        description: row.description || '',
        employee: employee?.C_BPartner_ID || 0,
        charge: charge?.id || 0,
        chargeAmount: parseFloat(row.chargeAmount) || 0,
        startDate: row.startDate || null,
        recurring: row.recurring === 'true' || row.recurring === true
      };
    });

    const insertFn = (item: any) =>
      this.type === 'deduction'
        ? this.importDataService.insertDeductionDetailsFromCSV(item)
        : this.importDataService.insertAdjustmentDetailsFromCSV(item);

    from(mapped).pipe(
      concatMap(item =>
        of(item).pipe(
          delay(500),
          concatMap(i =>
            insertFn(i).pipe(
              tap({
                next: () => {
                  insertedCount++;
                  this.insertionMessage = `Inserting ${label} ${insertedCount} of ${this.totalItems}, please wait...`;
                },
                error: () => {
                  this.insertionMessage = `Error inserting ${label} record. Skipping...`;
                }
              })
            )
          )
        )
      )
    ).subscribe({
      error: () => {
        this.insertionMessage = `Error during insertion. Please try again.`;
        this.isInserting = false;
        this.isLoading = false;
      },
      complete: () => {
        if (insertedCount === this.totalItems) {
          this.insertionMessage = `All ${this.totalItems} ${label} record(s) inserted successfully.`;
          this.isImported = true;
        } else {
          this.insertionMessage += ` ${this.totalItems - insertedCount} insertion(s) failed.`;
        }
        this.isInserting = false;
        this.isLoading = false;
      }
    });
  }

  onClose(): void {
    this.activeModal.dismiss('cancel');
    window.location.reload();
  }
}
